import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AppLayout } from 'src/components/layout/app-layout';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { useIsMobile } from 'src/hooks/useMobile';
import { useGetSurveyGroupPreviewQuery, useGetSurveyGroupResultsQuery } from 'src/redux/survey/survey-groups.endpoints';
import { SurveySummaryData, SurveyStatus } from 'src/redux/survey/survey.types';
import { SidebarProvider, SidebarTrigger } from 'src/components/ui/sidebar';
import { SurveyGroupSurveyFilterBar } from './components/survey-group-survey-filter-bar';
import ResultsQuestionList from '../view-survey/components/results-question-list.component';
import {
  aggregateGroupPayloadTotalUsers,
  sumGroupPayloadUserTotals,
} from '../view-survey/view-survey.helpers';
import { combinedQuestionsFromGroupResultsPayload } from '../view-survey/survey-group-results-merge.helpers';
import { SurveyGroupResultsSidebar } from './survey-group-results-sidebar';
import { useSurveyGroupResultsTabs } from './survey-group-results-nav';
import SurveyGroupCompareLoadingBar from './components/survey-group-compare-loading-bar';
import { useSurveyGroupCompareLoadingProgress } from './use-survey-group-compare-loading-progress';

const SurveyGroupResultsPage = (): ReactElement => {
  useSetPageTitle('Survey group results');
  const { clientId, surveyGroupId } = useParams<{ clientId?: string; surveyGroupId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useIsMobile();
  const { tabs, activeTab } = useSurveyGroupResultsTabs(surveyGroupId, clientId);

  const { data: groupResults, currentData, isFetching, isError } = useGetSurveyGroupResultsQuery(
    {
      groupId: surveyGroupId ?? '',
      include: 'stats,summary,trends,trends_events',
    },
    { skip: !surveyGroupId }
  );

  /** Avoid showing merged `data` from a prior render while this query is still pending (RTK lastResult). */
  const resolvedGroupResults = currentData ?? (!isFetching ? groupResults : undefined);

  const { data: groupPreview } = useGetSurveyGroupPreviewQuery(surveyGroupId ?? '', {
    skip: !surveyGroupId,
  });

  const [selectedGroupSurveyIds, setSelectedGroupSurveyIds] = useState<string[]>(() => {
    const p = searchParams.get('groupSurveys');
    return p ? p.split(',').filter(Boolean) : [];
  });

  useEffect(() => {
    if (!resolvedGroupResults?.surveys?.length) {
      return;
    }
    setSelectedGroupSurveyIds((prev) => {
      if (prev.length > 0) {
        return prev;
      }
      return resolvedGroupResults.surveys.map((s) => s.ext_survey_id);
    });
  }, [resolvedGroupResults]);

  const updateGroupSurveySelection = useCallback(
    (ids: string[]) => {
      setSelectedGroupSurveyIds(ids);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (ids.length) {
            next.set('groupSurveys', ids.join(','));
          } else {
            next.delete('groupSurveys');
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const visibleGroupSurveys = useMemo(() => {
    if (!resolvedGroupResults?.surveys?.length) {
      return [];
    }
    const selected = new Set(selectedGroupSurveyIds);
    return resolvedGroupResults.surveys.filter((s) => selected.has(s.ext_survey_id));
  }, [resolvedGroupResults, selectedGroupSurveyIds]);

  const showSummaries = useMemo(() => {
    return visibleGroupSurveys.some((p) => {
      const s = p.summary as SurveySummaryData | undefined;
      const q = s?.questions;
      if (!q) {
        return false;
      }
      return Array.isArray(q) ? q.length > 0 : Object.keys(q as object).length > 0;
    });
  }, [visibleGroupSurveys]);

  const questions = useMemo(() => {
    if (!resolvedGroupResults) {
      return [];
    }
    return combinedQuestionsFromGroupResultsPayload(resolvedGroupResults, showSummaries, groupPreview);
  }, [resolvedGroupResults, showSummaries, groupPreview]);

  const isGroupResultsLoading = isFetching && resolvedGroupResults === undefined;

  const { visible: groupResultsLoadBarVisible, percent: groupResultsLoadPercent } =
    useSurveyGroupCompareLoadingProgress(isGroupResultsLoading);

  const aggregatedUserTotal = useMemo(
    () => sumGroupPayloadUserTotals(visibleGroupSurveys),
    [visibleGroupSurveys]
  );

  const aggregatedTotalUsers = useMemo(
    () => aggregateGroupPayloadTotalUsers(visibleGroupSurveys),
    [visibleGroupSurveys]
  );

  return (
    <AppLayout
      activePage="/results"
      navbarProps={{
        showBackButton: false,
        backPath: clientId ? `/client/${clientId}/reports` : '/surveys',
        tabs,
        activeTab,
        onTabChange: () => undefined,
      }}
    >
      <div className="flex h-[calc(100vh-8rem)] min-h-0 w-full flex-col">
        <SidebarProvider className="min-h-0 flex-1 flex-row">
          <div className="min-w-0 flex-1 p-4 md:p-1">
            {isMobile && (
              <div className="flex size-12 items-center justify-center">
                <SidebarTrigger />
              </div>
            )}
            {!surveyGroupId ? (
              <p className="text-sm text-gray-600">
                Missing survey group.
              </p>
            ) : null}
            {groupResultsLoadBarVisible || isGroupResultsLoading ? (
              <SurveyGroupCompareLoadingBar
                percent={groupResultsLoadPercent}
                visible={groupResultsLoadBarVisible || isGroupResultsLoading}
                title="Loading survey group results…"
                helperText="Fetching aggregated survey group data from the server. Progress is estimated until the response arrives."
              />
            ) : null}
            {isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-sm text-red-800">
                Could not load survey group results. Check that you have access to this group and try again.
              </div>
            ) : null}
            {resolvedGroupResults?.surveys?.length ? (
              <>
                <SurveyGroupSurveyFilterBar
                  surveys={resolvedGroupResults.surveys}
                  selectedExtSurveyIds={selectedGroupSurveyIds}
                  onChange={updateGroupSurveySelection}
                />
                {visibleGroupSurveys.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    Select at least one survey above to show results.
                  </p>
                ) : (
                  <ResultsQuestionList
                    questions={questions}
                    surveyId={visibleGroupSurveys[0]?.ext_survey_id ?? ''}
                    audienceId=""
                    isShare={false}
                    groupAggregatedUserTotal={aggregatedUserTotal}
                    surveyStatusOverride={SurveyStatus.Complete}
                    hideAnswerLetterPrefix
                  />
                )}
              </>
            ) : null}
          </div>
          {surveyGroupId && resolvedGroupResults ? (
            <SurveyGroupResultsSidebar
              surveyGroupId={surveyGroupId}
              surveyGroupTitle={groupPreview?.name?.trim() || 'Survey group'}
              groupResults={resolvedGroupResults}
              mergedQuestions={questions}
              aggregatedTotalUsers={aggregatedTotalUsers}
              showSummaries={showSummaries}
            />
          ) : null}
        </SidebarProvider>
      </div>
    </AppLayout>
  );
};

export default SurveyGroupResultsPage;
